import { type FC, type ReactNode, useEffect, useState } from "react";

import { Colors, Tooltip } from "@snapchat/snap-design-system";
import { DownloadIcon, EllipsisIcon, InformationIcon, Sizes } from "@snapchat/snap-design-system-icons";
import DOMPurify from "dompurify";
import { useIntl } from "react-intl";

import { formatSize } from "../utils/formatSize";

import {
    PreviewBody,
    PreviewBottomRow,
    PreviewCardRoot,
    PreviewCreator,
    PreviewDescription,
    PreviewImage,
    PreviewImageArea,
    PreviewInfoButton,
    PreviewInstallButton,
    PreviewMenu,
    PreviewPills,
    PreviewPlatformTag,
    PreviewSize,
    PreviewTitle,
} from "./AssetSubmission.styles";
import {
    ActionInstall,
    PreviewAllPlatforms,
    PreviewAssetName,
    PreviewCreator as PreviewCreatorLine,
    PreviewNoDescription,
    PreviewYou,
} from "./messages.generated";

interface AssetPreviewCardProps {
    name?: string;
    creator?: string;
    image?: File;
    sizeBytes?: number;
    actionLabel?: ReactNode;
    description?: string;
}

// Only what the Quill toolbar can produce; strips scripts/handlers from anything else.
const sanitizeDescription = (html: string): string =>
    DOMPurify.sanitize(html, {
        ALLOWED_TAGS: [
            "h1",
            "h2",
            "h3",
            "p",
            "br",
            "strong",
            "em",
            "u",
            "s",
            "ol",
            "ul",
            "li",
            "blockquote",
            "pre",
            "code",
            "a",
            "span",
        ],
        // No target: Quill emits target="_blank" without rel, enabling reverse tabnabbing.
        ALLOWED_ATTR: ["href"],
    });

export const AssetPreviewCard: FC<AssetPreviewCardProps> = ({
    name,
    creator,
    image,
    sizeBytes,
    actionLabel = <ActionInstall />,
    description,
}) => {
    const intl = useIntl();
    // Tooltip trigger needs an accessible name; a <FormattedMessage/> can't be an aria-label string.
    const infoLabel = intl.formatMessage({
        id: "apps.assets.components.previewInfoLabel",
        defaultMessage: "View asset description",
    });

    const [imageUrl, setImageUrl] = useState<string>();

    useEffect(() => {
        if (image instanceof File && image.name) {
            const url = URL.createObjectURL(image);
            setImageUrl(url);
            return () => {
                URL.revokeObjectURL(url);
            };
        }
        setImageUrl(undefined);
        return;
    }, [image]);

    return (
        <PreviewCardRoot>
            <PreviewImageArea>
                {imageUrl && <PreviewImage alt={name} src={imageUrl} />}
                <PreviewMenu>
                    <EllipsisIcon size={Sizes.Small} />
                </PreviewMenu>
            </PreviewImageArea>
            <PreviewBody>
                <PreviewTitle>{name?.trim() ? name : <PreviewAssetName />}</PreviewTitle>
                <PreviewCreator>
                    <PreviewCreatorLine creator={creator?.trim() ? creator : <PreviewYou />} />
                </PreviewCreator>
                <PreviewPills>
                    <PreviewPlatformTag>
                        <PreviewAllPlatforms />
                    </PreviewPlatformTag>
                </PreviewPills>
                <PreviewSize>{formatSize(sizeBytes)}</PreviewSize>
                <PreviewBottomRow>
                    <Tooltip
                        content={
                            description ? (
                                // eslint-disable-next-line react/no-danger -- sanitized above
                                <PreviewDescription
                                    dangerouslySetInnerHTML={{ __html: sanitizeDescription(description) }}
                                />
                            ) : (
                                <PreviewNoDescription />
                            )
                        }
                    >
                        <PreviewInfoButton type="button" aria-label={infoLabel}>
                            <InformationIcon size={Sizes.Medium} />
                        </PreviewInfoButton>
                    </Tooltip>
                    <PreviewInstallButton>
                        {actionLabel}
                        <DownloadIcon fill={Colors.WHITE} size={Sizes.Medium} />
                    </PreviewInstallButton>
                </PreviewBottomRow>
            </PreviewBody>
        </PreviewCardRoot>
    );
};
